import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronDown,
  Target,
  AlertCircle,
  Plus,
  Search,
  X,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { api } from "@/api/client";

const TOGGLE_STORAGE_KEY = "dc_pickers_show_all";

function readPref() {
  try {
    return localStorage.getItem(TOGGLE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}
function writePref(v) {
  try {
    localStorage.setItem(TOGGLE_STORAGE_KEY, v ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/**
 * Unified employee multi-select.
 *
 * Behaviour:
 *  - Default: lists only the employees currently in `distribution_scope` and
 *    flags every option with a green target. Empty scope → falls back to
 *    "show all".
 *  - "Show all" toggle (persisted in localStorage) appends the remaining
 *    employees under a divider, marked with a grey "outside scope" badge.
 *  - "Add manually" input lets a power user pull anyone from the canonical
 *    list (irrespective of scope). Manually-added selections render with
 *    the warning badge so downstream charts can paint them differently.
 *
 * Props:
 *  - value, onChange       : controlled selection (array of employee_id)
 *  - sourceReport          : optional source filter (Z## vs 001-501)
 *  - departmentIds         : optional array of department ids to narrow the list
 *  - max                   : selection cap (default Infinity)
 *  - showToggle            : render the "Show all" switch (default true)
 *  - allowManualAdd        : render the "Add manually" autocomplete (default true)
 *  - placeholder, testId
 */
export default function EmployeesPicker({
  value = [],
  onChange,
  sourceReport,
  departmentIds,
  max = Infinity,
  showToggle = true,
  allowManualAdd = true,
  placeholder,
  testId = "employees-picker",
  className,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(readPref);
  const [scopeEmps, setScopeEmps] = useState([]); // in-scope list
  const [allEmps, setAllEmps] = useState([]); // full list (loaded on demand)
  const [allLoading, setAllLoading] = useState(false);
  const [manualQuery, setManualQuery] = useState("");
  const lastFetch = useRef({});

  // Persist toggle changes across the whole app session.
  const toggleShowAll = (v) => {
    setShowAll(v);
    writePref(v);
  };

  // Fetch in-scope list whenever sourceReport or departmentIds changes.
  const deptKey = useMemo(
    () => (Array.isArray(departmentIds) ? [...departmentIds].sort().join(",") : ""),
    [departmentIds]
  );
  useEffect(() => {
    let alive = true;
    const params = { in_scope_only: true };
    if (sourceReport) params.source_report = sourceReport;
    if (deptKey) params.department_ids = deptKey;
    api
      .get("/employees", { params })
      .then((r) => alive && setScopeEmps(r.data || []))
      .catch(() => alive && setScopeEmps([]));
    return () => {
      alive = false;
    };
  }, [sourceReport, deptKey]);

  // Lazy-load the full list when the toggle flips ON or manual add is open.
  const ensureAllLoaded = useMemo(
    () => async () => {
      const key = `${sourceReport || "all"}::${deptKey}`;
      if (lastFetch.current[key]) return;
      setAllLoading(true);
      try {
        const params = { in_scope_only: false };
        if (sourceReport) params.source_report = sourceReport;
        if (deptKey) params.department_ids = deptKey;
        const { data } = await api.get("/employees", { params });
        setAllEmps(data || []);
        lastFetch.current[key] = true;
      } catch {
        setAllEmps([]);
      } finally {
        setAllLoading(false);
      }
    },
    [sourceReport, deptKey]
  );

  useEffect(() => {
    if (showAll || (allowManualAdd && open)) ensureAllLoaded();
  }, [showAll, open, allowManualAdd, ensureAllLoaded]);

  // Index "all" by id for fast lookups when rendering selected chips.
  const allById = useMemo(() => {
    const map = {};
    (allEmps.length ? allEmps : scopeEmps).forEach((e) => {
      map[e.employee_id] = e;
    });
    // Scope wins on conflicts (more reliable in_distribution_scope flag).
    scopeEmps.forEach((e) => {
      map[e.employee_id] = e;
    });
    return map;
  }, [allEmps, scopeEmps]);

  const inScopeIds = useMemo(
    () => new Set(scopeEmps.map((e) => e.employee_id)),
    [scopeEmps]
  );

  // The "outside scope" list is only used in two places:
  //  - the popover "show all" section
  //  - the manual-add autocomplete results
  const outsideScopeEmps = useMemo(
    () => allEmps.filter((e) => !inScopeIds.has(e.employee_id)),
    [allEmps, inScopeIds]
  );

  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const atMax = (value?.length || 0) >= max;

  const toggleOne = (eid) => {
    const next = new Set(selectedSet);
    if (next.has(eid)) next.delete(eid);
    else if (!atMax) next.add(eid);
    onChange(Array.from(next));
  };

  // Phase 9.8b — tri-state "Seleccionar todos" over the currently-visible
  // list (scope + outside-scope if `showAll`). We operate ONLY on visible
  // options, so ids the user manually added from a different scope stay put.
  const visibleIds = useMemo(() => {
    const ids = scopeEmps.map((e) => e.employee_id);
    if (showAll) ids.push(...outsideScopeEmps.map((e) => e.employee_id));
    return ids;
  }, [scopeEmps, outsideScopeEmps, showAll]);

  const visibleSelectedCount = useMemo(
    () => visibleIds.filter((id) => selectedSet.has(id)).length,
    [visibleIds, selectedSet]
  );

  const allChecked =
    visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const someChecked =
    visibleSelectedCount > 0 && visibleSelectedCount < visibleIds.length;
  const selectAllState = allChecked
    ? true
    : someChecked
      ? "indeterminate"
      : false;

  const toggleSelectAll = () => {
    // If everything visible is already selected → clear the visible slice.
    // Otherwise → add the missing visible ids, respecting the `max` cap.
    const next = new Set(selectedSet);
    if (allChecked) {
      visibleIds.forEach((id) => next.delete(id));
    } else {
      for (const id of visibleIds) {
        if (next.size >= max) break;
        next.add(id);
      }
    }
    onChange(Array.from(next));
  };

  const selectedChips = (value || []).map((eid) => {
    const e = allById[eid];
    const inScope = inScopeIds.has(eid);
    return {
      eid,
      label: e ? `${e.employee_id} · ${e.employee_name}` : eid,
      inScope,
      outsideScope: !inScope && (e ? true : false),
      unknown: !e,
    };
  });

  // Manual-add autocomplete results: filter the full list against the query.
  const manualResults = useMemo(() => {
    const q = manualQuery.trim().toLowerCase();
    if (!q) return [];
    const list = allEmps.length ? allEmps : scopeEmps;
    return list
      .filter(
        (e) =>
          !selectedSet.has(e.employee_id) &&
          (e.employee_id.toLowerCase().includes(q) ||
            (e.employee_name || "").toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [manualQuery, allEmps, scopeEmps, selectedSet]);

  const trigger = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      data-testid={testId}
      className={cn(
        "h-auto min-h-9 w-full justify-between px-2 py-1.5 text-left font-normal",
        className
      )}
    >
      <div className="flex flex-1 flex-wrap items-center gap-1 overflow-hidden">
        {selectedChips.length === 0 ? (
          <span className="text-muted-foreground text-sm">
            {placeholder || t("picker.placeholder")}
          </span>
        ) : (
          selectedChips.slice(0, 4).map((c) => (
            <Badge
              key={c.eid}
              variant={c.outsideScope ? "outline" : "secondary"}
              className={cn(
                "font-normal text-xs",
                c.outsideScope &&
                  "border-amber-500/50 bg-amber-50/60 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              )}
              data-testid={`picker-chip-${c.eid}`}
            >
              {c.outsideScope && <AlertCircle className="mr-1 h-3 w-3" />}
              {c.label.length > 28 ? `${c.label.slice(0, 28)}…` : c.label}
            </Badge>
          ))
        )}
        {selectedChips.length > 4 && (
          <Badge variant="outline" className="text-xs">
            +{selectedChips.length - 4}
          </Badge>
        )}
      </div>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </Button>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] min-w-[320px] p-0"
          align="start"
        >
          {/* Header: count + toggle */}
          {showToggle && (
            <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-emerald-500" />
                <span className="text-xs font-medium">
                  {t("picker.inDistribution", { n: scopeEmps.length })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor={`${testId}-toggle`}
                  className="text-[11px] text-muted-foreground"
                >
                  {t("picker.showAll")}
                </Label>
                <Switch
                  id={`${testId}-toggle`}
                  checked={showAll}
                  onCheckedChange={toggleShowAll}
                  data-testid={`${testId}-toggle`}
                />
              </div>
            </div>
          )}

          {/* Phase 9.8b — Master "Seleccionar todos" (tri-state). Acts on the
              currently-visible list; respects the `max` cap. */}
          {visibleIds.length > 0 && (
            <div
              className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-1.5"
              data-testid={`${testId}-select-all-row`}
            >
              <button
                type="button"
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-left"
                data-testid="picker-select-all"
                aria-checked={allChecked ? "true" : someChecked ? "mixed" : "false"}
              >
                <Checkbox
                  checked={selectAllState}
                  className="pointer-events-none"
                />
                <span className="text-[11px] font-medium">
                  {t("picker.selectAll")}
                </span>
              </button>
              <span className="text-[10px] tabular-nums text-muted-foreground">
                {visibleSelectedCount} / {visibleIds.length}
              </span>
            </div>
          )}

          <ScrollArea className="max-h-72">
            <div className="p-1">
              {/* In-scope section */}
              {scopeEmps.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted-foreground">
                  {t("picker.empty")}
                </div>
              )}
              {scopeEmps.map((e) => {
                const isSelected = selectedSet.has(e.employee_id);
                return (
                  <button
                    key={e.employee_id}
                    type="button"
                    disabled={!isSelected && atMax}
                    onClick={() => toggleOne(e.employee_id)}
                    data-testid={`${testId}-option-${e.employee_id}`}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent",
                      !isSelected && atMax && "opacity-40 cursor-not-allowed"
                    )}
                  >
                    <Checkbox checked={isSelected} className="pointer-events-none" />
                    <Target className="h-3 w-3 shrink-0 text-emerald-500" />
                    <span className="flex-1 truncate">
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {e.employee_id}
                      </span>
                      {" · "}
                      {e.employee_name}
                    </span>
                    {isSelected && <Check className="h-3.5 w-3.5 opacity-70" />}
                  </button>
                );
              })}

              {/* Outside-scope section (only when toggle is ON) */}
              {showAll && outsideScopeEmps.length > 0 && (
                <>
                  <div className="my-1 flex items-center gap-2 px-2 py-1">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t("picker.others", { n: outsideScopeEmps.length })}
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  {outsideScopeEmps.map((e) => {
                    const isSelected = selectedSet.has(e.employee_id);
                    return (
                      <button
                        key={e.employee_id}
                        type="button"
                        disabled={!isSelected && atMax}
                        onClick={() => toggleOne(e.employee_id)}
                        data-testid={`${testId}-option-${e.employee_id}`}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent",
                          !isSelected && atMax && "opacity-40 cursor-not-allowed"
                        )}
                      >
                        <Checkbox checked={isSelected} className="pointer-events-none" />
                        <AlertCircle className="h-3 w-3 shrink-0 text-amber-500" />
                        <span className="flex-1 truncate text-muted-foreground">
                          <span className="font-mono text-[10px]">{e.employee_id}</span>
                          {" · "}
                          {e.employee_name}
                        </span>
                        {isSelected && <Check className="h-3.5 w-3.5 opacity-70" />}
                      </button>
                    );
                  })}
                </>
              )}

              {allLoading && (
                <div className="px-3 py-2 text-xs text-muted-foreground">
                  {t("common.loading")}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Manual-add autocomplete */}
          {allowManualAdd && (
            <div className="border-t bg-card/50 p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={manualQuery}
                  onChange={(e) => setManualQuery(e.target.value)}
                  placeholder={t("picker.addManually")}
                  className="h-8 pl-7 text-xs"
                  data-testid={`${testId}-manual-input`}
                />
              </div>
              {manualResults.length > 0 && (
                <div className="mt-1 max-h-32 overflow-y-auto rounded border bg-popover">
                  {manualResults.map((e) => (
                    <button
                      key={e.employee_id}
                      type="button"
                      onClick={() => {
                        toggleOne(e.employee_id);
                        setManualQuery("");
                      }}
                      className="flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-accent"
                      data-testid={`${testId}-manual-result-${e.employee_id}`}
                    >
                      <Plus className="h-3 w-3 shrink-0" />
                      {e.in_distribution_scope ? (
                        <Target className="h-3 w-3 shrink-0 text-emerald-500" />
                      ) : (
                        <AlertCircle className="h-3 w-3 shrink-0 text-amber-500" />
                      )}
                      <span className="flex-1 truncate">
                        <span className="font-mono text-muted-foreground">
                          {e.employee_id}
                        </span>
                        {" · "}
                        {e.employee_name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="mt-1 cursor-help text-[10px] text-muted-foreground">
                    {t("picker.outsideScopeHint")}
                  </p>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  {t("picker.outsideScopeTooltip")}
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Selected count footer */}
          <div className="flex items-center justify-between border-t px-3 py-2 text-[11px] text-muted-foreground">
            <span data-testid={`${testId}-count`}>
              {t("picker.selected", { n: value?.length || 0, max: max === Infinity ? "∞" : max })}
            </span>
            {(value?.length || 0) > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 px-2 text-[11px]"
                onClick={() => onChange([])}
                data-testid={`${testId}-clear`}
              >
                <X className="h-3 w-3" />
                {t("common.clear")}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}

/**
 * Helper for downstream charts that need to know which selected ids fell
 * outside the active distribution_scope. Returns Set<string>.
 */
export function useOutsideScopeIds(selectedIds, sourceReport) {
  const [outside, setOutside] = useState(new Set());
  useEffect(() => {
    let alive = true;
    if (!selectedIds || selectedIds.length === 0) {
      setOutside(new Set());
      return undefined;
    }
    const params = { in_scope_only: true };
    if (sourceReport) params.source_report = sourceReport;
    api
      .get("/employees", { params })
      .then((r) => {
        if (!alive) return;
        const inScope = new Set((r.data || []).map((e) => e.employee_id));
        setOutside(new Set(selectedIds.filter((id) => !inScope.has(id))));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [JSON.stringify(selectedIds), sourceReport]);
  return outside;
}
